// Thin fetch wrapper — same-origin, includes cookies, throws on non-2xx
// with the parsed body as `cause` so TanStack Query and React Hook Form can
// surface server-side validation errors verbatim.

import { activeCompanyStore } from './active-company-store';

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `Request failed with status ${status}`);
    this.status = status;
    this.body = body;
  }
}


interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  headers?: Record<string, string>;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const activeCompanyId = activeCompanyStore.get();
  const response = await fetch(path, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(activeCompanyId ? { 'X-Company-Id': String(activeCompanyId) } : {}),
      ...opts.headers,
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  // 204 No Content → return undefined as T
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const text = await response.text();
  const json = text ? safeJsonParse(text) : undefined;

  if (!response.ok) {
    const message =
      extractErrorMessage(json) ?? `${response.status} ${response.statusText}`.trim();
    throw new ApiError(response.status, json, message);
  }

  return json as T;
}

// Nest's exception filter returns `message` as a string for most errors, but
// the global ValidationPipe returns an array of per-constraint strings. Flatten
// both so DTO validation failures reach the UI instead of a bare status code.
function extractErrorMessage(json: unknown): string | null {
  if (!json || typeof json !== 'object' || !('message' in json)) return null;
  const message = (json as { message: unknown }).message;
  if (typeof message === 'string') return message || null;
  if (Array.isArray(message)) {
    const parts = message.filter((m): m is string => typeof m === 'string' && m.length > 0);
    return parts.length ? parts.join('; ') : null;
  }
  return null;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: <T>(path: string, headers?: Record<string, string>) =>
    request<T>(path, { method: 'GET', headers }),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(path, { method: 'POST', body, headers }),
  patch: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(path, { method: 'PATCH', body, headers }),
  delete: <T>(path: string, headers?: Record<string, string>) =>
    request<T>(path, { method: 'DELETE', headers }),
};
